import * as React from 'react'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import Collapse from '@mui/material/Collapse'
import Avatar from '@mui/material/Avatar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { red } from '@mui/material/colors'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { RequestBacklog, RequestStatus } from '../../types/Work/Request'
import { formatLocaleShortDateTime } from '../../services/CommonServices/DataFormatters'
import ApprovalIcon from '@mui/icons-material/Approval'
import { Box, Button, ButtonBase, Slide } from '@mui/material'
import { useQCContext } from './QCContext'
import BeenhereIcon from '@mui/icons-material/Beenhere'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import { ActionDialog } from './ActionDialog'
import { ReassignmentDialog } from '../Inspections/RequestReassignmentDialog'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import { getAttachmentsOfRequest } from '../../services/requestService'
import ImageModal from '../core/ImageModal'
interface RequestCardProps {
	children?: React.ReactNode
	request: RequestBacklog
	loadParentData?: () => Promise<void>
	showAssignee?: boolean
	expandedFromNotification?: boolean
}
export function RequestCard({
	children,
	request,
	loadParentData,
	showAssignee,
	expandedFromNotification,
}: RequestCardProps) {
	const [expanded, setExpanded] = React.useState(false)
	const [requestAction, setRequestAction] = React.useState<RequestStatus>('Pending')
	const [actionVisible, setActionVisible] = React.useState(false)
	const { setRequest, hierarchyPath, task } = useQCContext()
	const { projectTimeZone } = useProjectFeaturesContext()

	React.useEffect(() => {
		setRequest(request)
	}, [])

	// Handle initial expanded state and changes
	React.useEffect(() => {
		if (expandedFromNotification) {
			setExpanded(true)
		}
	}, [expandedFromNotification, request.id])

	const handleExpandClick = () => {
		setExpanded(!expanded)
	}

	function handleAction(
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		action: RequestStatus,
	): void {
		e.stopPropagation()
		e.preventDefault()
		setRequestAction(action)
		setActionVisible(true)
	}

	return (
		<Slide direction={'right'} in={true} timeout={500} mountOnEnter unmountOnExit>
			<Card sx={{ bgcolor: '#e0e0e0', height: 'max-content', mx: 4, flexShrink: 0 }}>
				<ButtonBase
					sx={{
						display: 'block',
						width: '100%',
						borderRadius: 2,
						overflow: 'hidden',
					}}
					focusRipple
				>
					<CardHeader
						sx={{ pb: 0 }}
						onClick={() => handleExpandClick()}
						avatar={
							<Avatar sx={{ bgcolor: red[500] }}>
								<ApprovalIcon />
							</Avatar>
						}
						action={
							<Box display={'flex'} flexDirection={'column'} gap={1}>
								{request.status === 'Pending' ? (
									<Box display={'flex'} gap={2}>
										<Button
											variant='contained'
											color='success'
											startIcon={<BeenhereIcon />}
											onClick={(e) => handleAction(e, 'Passed')}
										>
											Pass
										</Button>
										<Button
											variant='contained'
											color='error'
											startIcon={<ThumbDownIcon />}
											onClick={(e) => handleAction(e, 'Rework')}
										>
											Rework
										</Button>
										{showAssignee && (
											<ReassignmentDialog
												loadParentData={loadParentData!}
												request={request}
												taskName={hierarchyPath ? hierarchyPath.split('>').at(-1)! : ''}
											/>
										)}
									</Box>
								) : request.status === 'Rework' ? (
									<Button variant='contained' color='error' disabled startIcon={<ThumbDownIcon />}>
										Rework
									</Button>
								) : (
									<Button variant='contained' color='success' disabled startIcon={<BeenhereIcon />}>
										Pass
									</Button>
								)}
							</Box>
						}
						title={
							<Box display='flex' alignItems='start' width='90%'>
								<Typography variant='body2' sx={{ fontWeight: 'bold' }} textAlign={'start'}>
									{hierarchyPath}
								</Typography>
							</Box>
						}
						subheader={
							<Box sx={{ alignItems: 'start', display: 'flex', flexDirection: 'column' }}>
								<Typography variant='body2' color='textSecondary'>
									Requested At{' '}
									{formatLocaleShortDateTime(request.requestedAt, projectTimeZone ?? 'UTC')}
								</Typography>
								<Typography variant='body2' textAlign='start'>
									<b>Requested By {request.requestedBy}:</b> {request.comment}
								</Typography>
								{showAssignee && (
									<>
										<Typography variant='body2' textAlign='start' fontWeight={'bold'}>
											Assigned To {request.assignedTo}{' '}
										</Typography>
									</>
								)}
								<Box display={'flex'}>
									{request.inspectedAt && (
										<Typography variant='body2' color='textSecondary'>
											Inspected At{' '}
											{formatLocaleShortDateTime(request.inspectedAt, projectTimeZone ?? 'UTC')}
										</Typography>
									)}

									{request.hasAttachments && (
										<ImageModal
											itemId={request.id}
											padding={0}
											iconSize={'1.4rem'}
											imageUrlService={getAttachmentsOfRequest}
										/>
									)}
								</Box>
								{request.inspectionComment && request.inspectionComment !== '' && (
									<Typography variant='body2' textAlign='start' fontWeight={'bold'}>
										Inspection Comment: {request.inspectionComment}
									</Typography>
								)}
							</Box>
						}
					/>
					<CardActions
						sx={{ p: 0, minHeight: 'auto', display: 'flex', justifyContent: 'center' }}
						onClick={handleExpandClick}
					>
						<IconButton aria-expanded={expanded} aria-label='show more' sx={{ py: 0 }}>
							<ExpandMoreIcon />
						</IconButton>
					</CardActions>
				</ButtonBase>
				<Collapse in={expanded} timeout='auto' unmountOnExit>
					<CardContent>{children}</CardContent>
				</Collapse>

				<ActionDialog
					visible={actionVisible}
					passOrRework={requestAction}
					setVisible={setActionVisible}
					loadParentData={loadParentData}
					taskType={task?.reworks && task.reworks.length > 0 ? 'reworkTask' : 'workItemTask'}
				/>
			</Card>
		</Slide>
	)
}
